import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { LogLineText } from "./LogLineText";
import { CopyButton } from "@/components/ui/copy-button";
import { cn } from "@/lib/utils";
interface TestLogViewerProps {
  logs: string;
  duration?: number; // Duration in seconds (kept for backward compatibility)
  maxDuration?: number; // Max duration in suite (kept for backward compatibility)
  logLineStart?: number; // Starting line number (kept for backward compatibility)
  /**
   * - "scroll": fixed max height + internal scrolling (legacy behavior)
   * - "clamp": maxHeight clamp with internal scroll; can be expanded/collapsed
   */
  mode?: "scroll" | "clamp";
  /** Only used when mode="clamp". If not provided, fills available container space. */
  maxHeight?: number;
  /** Additional class names for the outer container */
  className?: string;
}
export const TestLogViewer = React.memo(function TestLogViewer(t0) {
  const $ = _c(51);
  const {
    logs,
    mode: t1,
    maxHeight,
    className
  } = t0;
  const mode = t1 === undefined ? "scroll" : t1;
  let t2;
  bb0: {
    if (!logs) {
      let t3;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = [];
        $[0] = t3;
      } else {
        t3 = $[0];
      }
      t2 = t3;
      break bb0;
    }
    let t3;
    if ($[1] !== logs) {
      t3 = logs.split("\n");
      $[1] = logs;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    t2 = t3;
  }
  const logLines = t2;
  const contentRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  let t3;
  if ($[3] !== isExpanded || $[4] !== mode) {
    t3 = () => {
      const el = contentRef.current;
      if (!el) {
        return;
      }
      if (mode !== "clamp" || isExpanded) {
        setIsOverflowing(false);
        return;
      }
      setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
    };
    $[3] = isExpanded;
    $[4] = mode;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const recomputeOverflow = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setIsExpanded(false);
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== logs || $[8] !== mode) {
    t5 = [mode, logs];
    $[7] = logs;
    $[8] = mode;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  useLayoutEffect(t4, t5);
  let t6;
  if ($[10] !== recomputeOverflow) {
    t6 = () => {
      recomputeOverflow();
    };
    $[10] = recomputeOverflow;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== logLines.length || $[13] !== maxHeight || $[14] !== recomputeOverflow) {
    t7 = [recomputeOverflow, logLines.length, maxHeight];
    $[12] = logLines.length;
    $[13] = maxHeight;
    $[14] = recomputeOverflow;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  useLayoutEffect(t6, t7);
  let t8;
  let t9;
  if ($[16] !== recomputeOverflow) {
    t8 = () => {
      const el_0 = contentRef.current;
      if (!el_0) {
        return;
      }
      const ro = new ResizeObserver(() => {
        recomputeOverflow();
      });
      ro.observe(el_0);
      return () => ro.disconnect();
    };
    t9 = [recomputeOverflow];
    $[16] = recomputeOverflow;
    $[17] = t8;
    $[18] = t9;
  } else {
    t8 = $[17];
    t9 = $[18];
  }
  useLayoutEffect(t8, t9);
  const useFlexFill = mode === "clamp" && maxHeight === undefined;
  const showClampToggle = mode === "clamp" && !useFlexFill && (isOverflowing || isExpanded);
  const isClickableToExpand = showClampToggle && !isExpanded;
  const t10 = useFlexFill && "flex flex-col min-h-0";
  let t11;
  if ($[19] !== className || $[20] !== t10) {
    t11 = cn("group relative border border-[var(--color-border)] dark:border-[var(--color-border)] rounded-lg overflow-hidden bg-white dark:bg-neutral-900", t10, className);
    $[19] = className;
    $[20] = t10;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== logLines.length || $[23] !== logs) {
    t12 = logLines.length > 0 && <CopyButton text={logs} title="Copy logs" className="z-10" stopPropagation={true} />;
    $[22] = logLines.length;
    $[23] = logs;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const t13 = isClickableToExpand && "cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800/50";
  const t14 = mode === "clamp" && maxHeight !== undefined && !isExpanded ? "overflow-hidden" : "overflow-y-auto";
  const t15 = useFlexFill && "flex-1 min-h-0";
  let t16;
  if ($[25] !== t13 || $[26] !== t14 || $[27] !== t15) {
    t16 = cn("bg-[#FAFAFA] dark:bg-[#111213] font-mono p-3 text-neutral-900 dark:text-neutral-100", t13, t14, t15);
    $[25] = t13;
    $[26] = t14;
    $[27] = t15;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== isExpanded || $[30] !== maxHeight || $[31] !== mode) {
    t17 = mode === "clamp" && !isExpanded && maxHeight !== undefined ? {
      maxHeight: `${maxHeight}px`
    } : undefined;
    $[29] = isExpanded;
    $[30] = maxHeight;
    $[31] = mode;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== isClickableToExpand) {
    t18 = isClickableToExpand ? e => {
      if (e.detail === 1) {
        setIsExpanded(true);
      }
    } : undefined;
    $[33] = isClickableToExpand;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  const t19 = isClickableToExpand ? "Click to expand logs" : undefined;
  let t20;
  if ($[35] !== logLines) {
    t20 = logLines.length === 0 ? <div className="flex items-center justify-center h-full text-sm text-neutral-400">No logs available</div> : <div>{logLines.map(_temp)}</div>;
    $[35] = logLines;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== t16 || $[38] !== t17 || $[39] !== t18 || $[40] !== t19 || $[41] !== t20) {
    t21 = <div id="test-log-viewer-content" ref={contentRef} className={t16} style={t17} onClick={t18} aria-label={t19}>{t20}</div>;
    $[37] = t16;
    $[38] = t17;
    $[39] = t18;
    $[40] = t19;
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== isExpanded || $[44] !== showClampToggle) {
    t22 = showClampToggle && <button type="button" aria-label={isExpanded ? "Collapse logs" : "Expand logs"} onClick={e_0 => {
      e_0.stopPropagation();
      setIsExpanded(_temp2);
    }} className="flex w-full items-center justify-center gap-1 py-2 text-xs font-medium text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 border-t border-[var(--color-border)] dark:border-[var(--color-border)] transition-colors cursor-pointer">{isExpanded ? <><span>Collapse</span><ChevronUp className="h-4 w-4" /></> : <><span>Expand</span><ChevronDown className="h-4 w-4" /></>}</button>;
    $[43] = isExpanded;
    $[44] = showClampToggle;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== t11 || $[47] !== t12 || $[48] !== t21 || $[49] !== t22) {
    t23 = <div className={t11}>{t12}{t21}{t22}</div>;
    $[46] = t11;
    $[47] = t12;
    $[48] = t21;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  return t23;
});
function _temp(line, index) {
  const trimmed = line.trim();
  const isDiffRemoved = trimmed.startsWith("-");
  const isDiffAdded = trimmed.startsWith("+");
  return <div key={index} className="min-h-[16px] text-xs whitespace-pre-wrap leading-4"><span className={isDiffRemoved ? "text-red-700 dark:text-red-300" : isDiffAdded ? "text-emerald-700 dark:text-emerald-300" : ""}><LogLineText text={line} searchTerms={[]} /></span></div>;
}
function _temp2(v) {
  return !v;
}